import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  useTheme,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  Close as CloseIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { ImportExportMAIQProps } from '../types/ComponentTypes';
import ExportSection from './ExportSection';
import ImportSection from './ImportSection';
import ImportSuccessModal from './ImportSuccessModal';
// ImportConfirmationModal removed since we handle confirmation inline now
import DownloadModalMAIQ from 'app/components/DownloadModalMAIQ';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { useImportExport } from '../context';
import ImportExportApiService from '../apiService/importExportApiService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`import-export-tabpanel-${index}`}
      aria-labelledby={`import-export-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ImportExportModal: React.FC<ImportExportMAIQProps & { 
  open: boolean; 
  onClose: () => void;
}> = ({
  type,
  open,
  onClose,
  onSuccess,
  onError,
  onImportComplete,
  onExportComplete,
  showImport = true,
  showExport = true,
  modalTitle,
  disabled = false
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const [activeTab, setActiveTab] = useState(0);
  
  const { exportData, exportLoading, exportError, handleImportCompareData, loading, compareData, setLoading, resetState } = useImportExport();
  
  // Ref to access ComparePreview component
  const comparePreviewRef = useRef<any>(null);
  
  // Download modal state - exactly like RunLogs
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadTitle, setDownloadTitle] = useState('');
  const [downloadFileName, setDownloadFileName] = useState('');
  
  // Success modal state
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  
  // Confirmation modal state - removed since we handle confirmation inline now

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Download modal functions - exactly like RunLogs
  const openDownloadModal = (url: string, title: string, fileName: string) => {
    setDownloadUrl(url);
    setDownloadTitle(title);
    setDownloadFileName(fileName);
    setDownloadModalOpen(true);
  };

  const closeDownloadModal = () => {
    setDownloadModalOpen(false);
    setDownloadUrl('');
    setDownloadTitle('');
    setDownloadFileName('');
  };

  // Effect to handle export data - exactly like RunLogs
  useEffect(() => {
    if (exportData && exportData instanceof Blob) {
      // Add a small delay to ensure blob is fully ready
      const timer = setTimeout(() => {
        try {
          // Revoke previous URL if exists
          if (downloadUrl) {
            URL.revokeObjectURL(downloadUrl);
          }
          
          // Create new blob URL
          const blobUrl = URL.createObjectURL(exportData);
          setDownloadUrl(blobUrl);
        } catch (error) {
        }
      }, 100); // Small delay to ensure blob is ready
      
      return () => clearTimeout(timer);
    }
  }, [exportData, downloadUrl]);

  // Cleanup effect for blob URL
  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const handleSuccess = (result: any) => {
    console.log('handleSuccess called with result:', result);
    
    if (onSuccess) {
      onSuccess(result);
    }
    if (result.type === 'export' && onExportComplete) {
      onExportComplete(result);
    }
    if (result.type === 'import' && onImportComplete) {
      onImportComplete(result);
    }
    
    // Show success modal for import results
    if (result.type === 'import') {
      setImportResult(result);
      setSuccessModalOpen(true);
    }
  };

  const handleError = (error: any) => {
    if (onError) {
      onError(error);
    }
  };

  const handleClose = () => {
    // Reset all local modal states
    setActiveTab(0);
    setSuccessModalOpen(false);
    setImportResult(null);
    
    // Reset confirmation modal states - removed since we handle confirmation inline now
    
    // Reset download modal states
    setDownloadModalOpen(false);
    setDownloadTitle('');
    setDownloadFileName('');
    
    // Cleanup blob URL to prevent memory leaks
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
      setDownloadUrl('');
    }
    
    // Reset all import/export context states
    resetState();
    
    onClose();
  };

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    setImportResult(null);
    onClose(); // Close the main modal too
  };

  // Confirmation modal handlers - removed since we handle confirmation inline now

  // Determine which tabs to show
  const tabs: Array<{ label: string; icon: React.ReactElement; index: number }> = [];
  if (showExport) tabs.push({ label: 'Export', icon: <ExportIcon />, index: 0 });
  if (showImport) tabs.push({ label: 'Import', icon: <ImportIcon />, index: tabs.length });

  const getTabIndex = (tabLabel: string) => {
    const tab = tabs.find(t => t.label === tabLabel);
    return tab ? tab.index : 0;
  };


  return (
    <>
      <ModalMAIQ open={open} onClose={handleClose} hideDefaultCloseButton={true}>
        <Paper
          elevation={0}
          sx={{
            width: '90vw',
            maxWidth: '1200px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: isInAIAvatarLayout
                ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                : '#0F2A52',
              color: isInAIAvatarLayout
                ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                : 'white',
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography variant="h6" fontWeight="600">
                {modalTitle || `${type.entityType.charAt(0).toUpperCase() + type.entityType.slice(1)} Import/Export`}
              </Typography>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: isInAIAvatarLayout
                    ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                    : 'white',
                  '&:hover': {
                    backgroundColor: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                      : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="import export tabs"
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 48,
                  color: isInAIAvatarLayout
                    ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                    : '#0F2A52',
                  '&.Mui-selected': {
                    color: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? '#000000' : '#000000')
                      : '#0F2A52',
                    backgroundColor: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)')
                      : 'rgba(15, 42, 82, 0.04)',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: isInAIAvatarLayout
                    ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                    : '#0F2A52',
                  height: 3,
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  disabled={disabled}
                />
              ))}
            </Tabs>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 0,
            }}
          >
            {showExport && (
              <TabPanel value={activeTab} index={getTabIndex('Export')}>
                <ExportSection
                  config={type}
                  onExport={() => {}} // Remove direct export callback
                  onError={handleError}
                  onOpenDownloadModal={openDownloadModal}
                  isInAIAvatarLayout={isInAIAvatarLayout}
                />
              </TabPanel>
            )}

            {showImport && (
              <TabPanel value={activeTab} index={getTabIndex('Import')}>
                <ImportSection
                  ref={comparePreviewRef}
                  config={type}
                  onImport={handleSuccess}
                  onError={handleError}
                  isInAIAvatarLayout={isInAIAvatarLayout}
                />
              </TabPanel>
            )}
          </Box>

          {/* Action Buttons for Import Tab - removed since we handle this inline in ImportSection now */}
        </Paper>
      </ModalMAIQ>
      
      {/* Download Modal */}
      <DownloadModalMAIQ
        open={downloadModalOpen}
        onClose={closeDownloadModal}
        downloadUrl={downloadUrl}
        title={downloadTitle}
        fileName={downloadFileName}
      />
      
      {/* Import Confirmation Modal - removed since we handle confirmation inline now */}
      
      {/* Success Modal */}
      {importResult && (
        <ImportSuccessModal
          open={successModalOpen}
          onClose={handleSuccessModalClose}
          importResult={importResult}
        />
      )}
    </>
  );
};

export default ImportExportModal;
